// FE-PINV-071 — List view sort.
//
// Sort runs client-side over the flattened Renewal Board rows so the
// click cycle (default → asc → desc → default) stays interactive
// without a per-click refetch.

import type { ListSort, ListSortField } from "./listUrlState";
import type { OpportunityListRow } from "./listRowFlatten";


const _STAGE_RANK: Record<string, number> = {
  lead: 0,
  meeting_set: 1,
  qualified: 2,
  proposal: 3,
  negotiation: 4,
  bor_signed_pending_effective_date: 5,
  closed_won: 6,
  closed_lost: 7,
};


const _TYPE_RANK: Record<string, number> = {
  new_business: 0,
  bor_takeover: 1,
  rfp_response: 2,
  expansion: 3,
};


const _LIVES_BAND_RANK: Record<string, number> = {
  micro: 0,
  small: 1,
  mid_market: 2,
  large: 3,
  enterprise: 4,
};


function _renewalSerial(r: OpportunityListRow): number {
  return r.renewal_year * 12 + r.renewal_month;
}


/** Compares two rows where the field may be null. Returns a tuple
 *  ``[cmp, nullAdjusted]``. ``nullAdjusted`` is the raw "null wins"
 *  sentinel (negative when a should sink, positive when b should sink,
 *  0 when both are real); the caller uses it to bypass direction
 *  multiplication so null sinks to the end regardless of asc/desc. */
function _compareNullable<T extends number | string>(
  a: T | null | undefined, b: T | null | undefined,
): { cmp: number; nullSink: number } {
  const aNull = a === null || a === undefined;
  const bNull = b === null || b === undefined;
  if (aNull && bNull) return { cmp: 0, nullSink: 0 };
  if (aNull) return { cmp: 0, nullSink: 1 };
  if (bNull) return { cmp: 0, nullSink: -1 };
  if (typeof a === "number" && typeof b === "number") {
    return { cmp: a - b, nullSink: 0 };
  }
  return { cmp: String(a).localeCompare(String(b)), nullSink: 0 };
}


interface FieldCompare {
  cmp: number;
  nullSink: number;
}


function _compareByField(
  field: ListSortField,
  a: OpportunityListRow,
  b: OpportunityListRow,
): FieldCompare {
  switch (field) {
    case "prospect":
      return {
        cmp: a.prospect_name.localeCompare(b.prospect_name), nullSink: 0,
      };
    case "opportunity":
      return {
        cmp: a.opportunity_name.localeCompare(b.opportunity_name),
        nullSink: 0,
      };
    case "stage":
      return {
        cmp: (_STAGE_RANK[a.stage] ?? 99) - (_STAGE_RANK[b.stage] ?? 99),
        nullSink: 0,
      };
    case "type":
      return {
        cmp: (
          (_TYPE_RANK[a.opportunity_type] ?? 99)
          - (_TYPE_RANK[b.opportunity_type] ?? 99)
        ),
        nullSink: 0,
      };
    case "lives": {
      // Bind to locals so TS narrows them across the early returns —
      // checking ``a.lives_band === null`` into a boolean variable does
      // not carry the narrowing back to the property access below.
      const aBand = a.lives_band;
      const bBand = b.lives_band;
      if (aBand === null && bBand === null) return { cmp: 0, nullSink: 0 };
      if (aBand === null) return { cmp: 0, nullSink: 1 };
      if (bBand === null) return { cmp: 0, nullSink: -1 };
      return {
        cmp: (
          (_LIVES_BAND_RANK[aBand] ?? 99)
          - (_LIVES_BAND_RANK[bBand] ?? 99)
        ),
        nullSink: 0,
      };
    }
    case "renewal":
      return {
        cmp: _renewalSerial(a) - _renewalSerial(b),
        nullSink: 0,
      };
    case "producer":
      return _compareNullable(a.producer_id, b.producer_id);
    case "est_arr":
      return _compareNullable(a.value_estimate, b.value_estimate);
    case "last_activity":
      return _compareNullable(a.last_activity_at, b.last_activity_at);
    case "next_step":
      return _compareNullable(a.next_step, b.next_step);
    case "alerts":
      return {
        cmp: a.form5500_alert_count - b.form5500_alert_count,
        nullSink: 0,
      };
  }
}


export function sortListRows(
  rows: OpportunityListRow[], sort: ListSort,
): OpportunityListRow[] {
  if (sort.field === null) return rows;
  const field = sort.field;
  const dir = sort.dir === "desc" ? -1 : 1;
  // Stable sort with a copy so the caller's array isn't mutated.
  const indexed = rows.map((r, i) => ({ r, i }));
  indexed.sort((a, b) => {
    const { cmp, nullSink } = _compareByField(field, a.r, b.r);
    if (nullSink !== 0) return nullSink;
    if (cmp !== 0) return cmp * dir;
    return a.i - b.i;
  });
  return indexed.map((x) => x.r);
}
